/* ============================================================================
   velocity.collection-v2.js — AFX Velocity v2 CRO card behaviours.

   Loaded as type="module" defer by static-collection.liquid when card_style ==
   velocity. Namespaced under .afxv-grid via event delegation so nothing global
   leaks and zero-card collections are a no-op. Three behaviours:
     1. qty stepper (delegated, ± and manual entry, 1..99)
     2. dense-view toggle (swaps the grid density class; persists the choice)
     3. AJAX add → POST /cart/add.js, then GET /cart.js, then dispatch the EXACT
        event the Empire header listens for: window 'cartcount:update' with the
        cart payload in detail. Degrades gracefully if anything fails.

   Cart contract: quantity POSTed = the stepper value. The product/variant IS the
   case (price = case price), so qty is NEVER multiplied by pack. `data-pack` is
   used only to report qty × pack UNITS in the success toast/aria.
   ========================================================================== */
(() => {
  'use strict';
  if (window.__afxvCollectionV2) return;
  window.__afxvCollectionV2 = true;

  const root = () => window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root
    : '/';
  const CART_ADD = () => `${root()}cart/add.js`;
  const CART_GET = () => `${root()}cart.js`;
  const VIEW_KEY = 'afxv_collection_view';
  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── helpers ──────────────────────────────────────────────────────────── */
  function clampQty(n) {
    n = parseInt(n, 10);
    if (!n || n < 1) n = 1;
    if (n > 99) n = 99;
    return n;
  }
  function stepperOf(el) { return el.closest('.afxv-stepper'); }
  function cardOf(el) { return el.closest('[data-afxv-card]'); }

  function reflectQty(stepper) {
    if (!stepper) return;
    const input = stepper.querySelector('[data-afxv-qty]');
    if (!input) return;
    const v = clampQty(input.value);
    input.value = v;
    const dec = stepper.querySelector('.afxv-dec');
    if (dec) dec.disabled = v <= 1;
    const card = cardOf(stepper);
    const lab = card && card.querySelector('[data-afxv-qtylab]');
    if (lab) lab.textContent = v > 1 ? `×${v}` : '';
  }

  /* ── stepper (delegated) ──────────────────────────────────────────────── */
  document.addEventListener('click', (e) => {
    const inc = e.target.closest('.afxv-grid .afxv-inc');
    const dec = e.target.closest('.afxv-grid .afxv-dec');
    if (!inc && !dec) return;
    const stepper = stepperOf(inc || dec);
    if (!stepper) return;
    const input = stepper.querySelector('[data-afxv-qty]');
    if (!input) return;
    let v = clampQty(input.value);
    v = inc ? Math.min(99, v + 1) : Math.max(1, v - 1);
    input.value = v;
    reflectQty(stepper);
  });

  document.addEventListener('change', (e) => {
    const input = e.target.closest('.afxv-grid [data-afxv-qty]');
    if (!input) return;
    input.value = clampQty(input.value);
    reflectQty(stepperOf(input));
  });

  /* ── toast (reused from any existing one, else a lightweight scoped node) ─ */
  let toastNode = null;
  let toastTimer = null;
  function ensureToast() {
    if (toastNode) return toastNode;
    toastNode = document.createElement('div');
    toastNode.className = 'afxv-toast';
    toastNode.setAttribute('role', 'status');
    toastNode.setAttribute('aria-live', 'polite');
    toastNode.style.cssText = [
      'position:fixed', 'left:50%', 'bottom:24px', 'transform:translate(-50%,140%)',
      'z-index:9999', 'background-color:var(--ink,#16110F)', 'color:var(--bg,#FAF6EF)',
      'padding:12px 16px', 'border-radius:var(--radius-md,12px)', 'box-shadow:var(--shadow-e3)',
      'font-family:var(--font-body,sans-serif)', 'font-size:14px', 'font-weight:600',
      'max-width:90vw', 'transition:transform var(--motion-base,320ms) var(--ease-out,ease)'
    ].join(';');
    document.body.appendChild(toastNode);
    return toastNode;
  }
  function showToast(msg) {
    const n = ensureToast();
    n.textContent = msg;
    if (prefersReduced) {
      // skip the slide-in/out animation; appear and disappear in place
      n.style.transition = 'none';
      n.style.transform = 'translate(-50%,0)';
    } else {
      // force a reflow so the transition runs from the hidden state, then reveal
      void n.offsetWidth;
      n.style.transform = 'translate(-50%,0)';
    }
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { n.style.transform = 'translate(-50%,140%)'; }, 3200);
  }

  /* ── notify the Empire header + drawer of the new cart ───────────────────
     Empire dispatches CustomEvent('cartcount:update', { detail: cartResponse })
     on window; its Header registers events.register(window, 'cartcount:update').
     We mirror that exactly so the header count + flyout stay in sync. */
  async function fetchCart() {
    try {
      const res = await fetch(CART_GET(), { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      return (data && typeof data.item_count === 'number') ? data : null;
    } catch (err) { return null; }
  }

  function broadcastCart(cart) {
    // Never dispatch with a null/garbage payload — Empire's header listener reads
    // event.detail.item_count directly and a null detail throws a TypeError, which
    // leaves the header silently stale after a SUCCESSFUL add. Bail if we don't
    // have a usable cart object.
    if (!cart || typeof cart.item_count !== 'number') return;
    try {
      window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
    } catch (err) { /* no-op */ }
    // Also dispatch the OS2.0 product-form event some drawers listen for.
    try {
      document.dispatchEvent(new CustomEvent('product-form:cart:update', { bubbles: true, detail: { cart } }));
    } catch (err) { /* no-op */ }
  }

  /* ── AJAX add (delegated) ────────────────────────────────────────────── */
  const ADD_ICON = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><path d="m5 13 4 4L19 7"/></svg>';

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.afxv-grid [data-afxv-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('data-state')) return;

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    const card = cardOf(btn);
    const pack = card ? parseInt(card.getAttribute('data-pack'), 10) || 0 : 0;
    const unitNoun = (card && card.getAttribute('data-unit-noun')) || 'unit';
    const input = card && card.querySelector('[data-afxv-qty]');
    const qty = input ? clampQty(input.value) : 1;

    const originalHTML = btn.innerHTML;
    btn.setAttribute('data-state', 'adding');
    btn.setAttribute('aria-busy', 'true');
    btn.innerHTML = '<span class="afxv-spin" aria-hidden="true"></span><span>Adding</span>';

    try {
      const addRes = await fetch(CART_ADD(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      });
      const addData = await addRes.json();
      if (!addRes.ok) throw addData;

      // pull the authoritative cart state for the header, with one retry — a flaky
      // cart.js GET must NOT leave the header stale after a successful add.
      let cart = await fetchCart();
      if (!cart || typeof cart.item_count !== 'number') {
        cart = await fetchCart();
      }
      broadcastCart(cart);

      btn.setAttribute('data-state', 'added');
      btn.removeAttribute('aria-busy');
      btn.innerHTML = `${ADD_ICON}<span>Added</span>`;
      // keep the accessible name in sync with the visible state (was "Add … to cart")
      const addedLabel = btn.getAttribute('data-label-added');
      if (addedLabel) btn.setAttribute('aria-label', addedLabel);

      const units = pack > 0 ? qty * pack : qty;
      const msg = pack > 0
        ? `Added ${qty} case${qty > 1 ? 's' : ''} · ${units} ${unitNoun}${units > 1 ? 's' : ''}`
        : `Added ${qty} to cart`;
      showToast(msg);

      setTimeout(() => {
        btn.removeAttribute('data-state');
        btn.innerHTML = originalHTML;
        const idleLabel = btn.getAttribute('data-label-idle');
        if (idleLabel) btn.setAttribute('aria-label', idleLabel);
      }, 1800);
    } catch (err) {
      btn.removeAttribute('data-state');
      btn.removeAttribute('aria-busy');
      btn.innerHTML = originalHTML;
      const detail = err && (err.description || err.message);
      showToast(detail ? `Could not add: ${detail}` : 'Could not add to cart. Please try again.');
      // eslint-disable-next-line no-console
      console.error('[afxv add]', err);
    }
  });

  /* ── view toggle (grid · list · dense) ────────────────────────────────────
     Grid/list/dense use DIFFERENT server-rendered markup (a dense row is not a
     grid card with a class swapped), so — like Empire's own toggle — we change
     the view by reloading with ?grid_list=<view>. The section reads that param
     (it is on the faceted-filter allow-list, so facets/sort survive). The grid↔
     list case ALSO repaints purely via the swapped container class while the
     reload is in flight, so the transition feels instant. */
  function currentServerView() {
    const grid = document.querySelector('[data-productgrid-items].afxv-grid');
    if (!grid) return 'grid-view';
    if (grid.classList.contains('afxv-grid--dense')) return 'dense-view';
    if (grid.classList.contains('afxv-grid--list')) return 'list-view';
    return 'grid-view';
  }

  function syncButtons(view) {
    document.querySelectorAll('[data-afxv-view]').forEach((b) => {
      const active = b.getAttribute('data-afxv-view') === view;
      b.classList.toggle('active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function gotoView(view) {
    try { localStorage.setItem(VIEW_KEY, view); } catch (err) { /* no-op */ }
    const url = new URL(window.location);
    url.searchParams.set('grid_list', view);
    window.location.assign(url.toString());
  }

  function initView() {
    syncButtons(currentServerView());
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-afxv-view]');
    if (!btn) return;
    e.preventDefault();
    const view = btn.getAttribute('data-afxv-view');
    if (!view || view === currentServerView()) return;

    // Instant visual feedback for the CSS-only grid↔list change before reload.
    if (view !== 'dense-view' && currentServerView() !== 'dense-view') {
      document.querySelectorAll('[data-productgrid-items].afxv-grid').forEach((grid) => {
        grid.classList.toggle('afxv-grid--list', view === 'list-view');
      });
    }
    syncButtons(view);
    gotoView(view);
  });

  /* ── boot + theme-editor re-render safety ─────────────────────────────── */
  function boot() {
    if (!document.querySelector('.afxv-grid')) return;
    initView();
    // normalise stepper button disabled states on load
    document.querySelectorAll('.afxv-grid .afxv-stepper').forEach(reflectQty);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init when the section is (re)loaded.
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector && e.target.querySelector('.afxv-grid')) boot();
  });

  // Theme editor: tear down our floating toast when the section is removed so it
  // doesn't linger as an orphan and no stray timer fires against a detached node.
  document.addEventListener('shopify:section:unload', (e) => {
    if (!e.target || !e.target.querySelector || !e.target.querySelector('.afxv-grid')) return;
    clearTimeout(toastTimer);
    toastTimer = null;
    if (toastNode && toastNode.parentNode) toastNode.parentNode.removeChild(toastNode);
    toastNode = null;
  });
})();
